import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { AtwReviewService } from '../../../../services/atw-review';

interface Review {
  id: number | string;
  avatar: string;
  name: string;
  stars: number;
  text: string;
  imageUrl?: string;
}

@Component({
  selector: 'app-allreviews',
  templateUrl: './allreviews.component.html',
  styleUrl: './allreviews.component.scss',
})
export class AllreviewsComponent implements OnInit, OnDestroy {
  readonly pageSize = 3;
  readonly starItems: number[] = [1, 2, 3, 4, 5];

  currentPage = 0;
  reviews: Review[] = [];

  isLoading = false;
  errorMessage = '';
  isAr = false;
  lang: 'ar' | 'en' = 'en';

  private serverPaginated = false;
  private apiTotalPages = 0;
  private isBrowser: boolean;
  private destroy$ = new Subject<void>();

  constructor(
    private reviewService: AtwReviewService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.lang = this.getCurrentLang();
    this.isAr = this.lang === 'ar';

    this.loadReviews(1);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get totalPages(): number {
    if (this.serverPaginated) {
      return this.apiTotalPages;
    }

    return Math.ceil(this.reviews.length / this.pageSize);
  }

  get pages(): number[] {
    return Array.from({ length: this.totalPages }, (_, index) => index);
  }

  get visibleReviews(): Review[] {
    if (this.serverPaginated) {
      return this.reviews;
    }

    const start = this.currentPage * this.pageSize;
    return this.reviews.slice(start, start + this.pageSize);
  }

  loadReviews(page: number = 1): void {
    this.isLoading = true;
    this.errorMessage = '';

    const filters = {
      status: 'active',
      lang: this.lang,
    };

    this.reviewService
      .getPaginatedReviews(page, this.pageSize, filters)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (response) => {
          const items = this.extractItems(response);

          this.serverPaginated = this.hasServerPagination(response);
          this.apiTotalPages = this.extractTotalPages(response, items.length);

          this.reviews = items.map((item: any, index: number) =>
            this.mapReview(item, index)
          );

          this.currentPage = Math.max(page - 1, 0);
        },
        error: () => {
          this.reviews = [];
          this.errorMessage = this.isAr
            ? 'حصلت مشكلة أثناء تحميل التقييمات.'
            : 'Something went wrong while loading reviews.';
        },
      });
  }

  nextReviews(): void {
    if (this.totalPages <= 1) {
      return;
    }

    const nextPage =
      this.currentPage + 1 >= this.totalPages ? 0 : this.currentPage + 1;

    this.goToPage(nextPage);
  }

  prevReviews(): void {
    if (this.totalPages <= 1) {
      return;
    }

    const prevPage =
      this.currentPage - 1 < 0 ? this.totalPages - 1 : this.currentPage - 1;

    this.goToPage(prevPage);
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages) {
      return;
    }

    if (this.serverPaginated) {
      this.loadReviews(page + 1);
      return;
    }

    this.currentPage = page;
  }

  trackByReview(index: number, review: Review): number | string {
    return review.id || index;
  }

  onAvatarImageError(review: Review): void {
    review.imageUrl = '';
  }

  private mapReview(item: any, index: number): Review {
    const name =
      this.pickLocalized(item, 'name') ||
      this.pick(item, [
        'reviewer_name',
        'client_name',
        'user_name',
        'author_name',
        'full_name',
        'username',
      ]) ||
      (this.isAr ? 'زائر' : 'Guest');

    const text =
      this.pickLocalized(item, 'text') ||
      this.pickLocalized(item, 'review') ||
      this.pickLocalized(item, 'comment') ||
      this.pickLocalized(item, 'message') ||
      this.pick(item, [
        'review_text',
        'description',
        'body',
        'content',
        'feedback',
      ]) ||
      '';

    const stars = this.normalizeStars(
      this.pick(item, ['stars', 'rating', 'rate'])
    );

    const imageUrl =
      this.pick(item, [
        'image_url',
        'avatar_url',
        'photo_url',
        'user_image_url',
        'image',
        'avatar',
        'photo',
      ]) || '';

    return {
      id: item?.id ?? index,
      avatar: this.getAvatarLetter(name),
      name,
      stars,
      text,
      imageUrl,
    };
  }

  private extractItems(response: any): any[] {
    const candidates = [
      response?.data?.data,
      response?.data?.items,
      response?.reviews?.data,
      response?.reviews,
      response?.data,
      response?.items,
      response,
    ];

    const found = candidates.find((candidate) => Array.isArray(candidate));

    return found || [];
  }

  private hasServerPagination(response: any): boolean {
    return !!(
      response?.data?.last_page ||
      response?.last_page ||
      response?.meta?.last_page ||
      response?.data?.meta?.last_page ||
      response?.pagination?.last_page ||
      response?.data?.pagination?.last_page ||
      response?.data?.total ||
      response?.total
    );
  }

  private extractTotalPages(response: any, itemsLength: number): number {
    const lastPage = Number(
      response?.data?.last_page ||
        response?.last_page ||
        response?.meta?.last_page ||
        response?.data?.meta?.last_page ||
        response?.pagination?.last_page ||
        response?.data?.pagination?.last_page ||
        0
    );

    if (lastPage > 0) {
      return lastPage;
    }

    const total = Number(
      response?.data?.total ||
        response?.total ||
        response?.meta?.total ||
        response?.data?.meta?.total ||
        0
    );

    const perPage = Number(
      response?.data?.per_page ||
        response?.per_page ||
        response?.meta?.per_page ||
        response?.data?.meta?.per_page ||
        this.pageSize
    );

    if (total > 0 && perPage > 0) {
      return Math.ceil(total / perPage);
    }

    return Math.ceil(itemsLength / this.pageSize);
  }

  private pickLocalized(item: any, key: string): string {
    const langKey = `${key}_${this.lang}`;
    const fallbackKey = `${key}_${this.lang === 'ar' ? 'en' : 'ar'}`;

    return this.cleanValue(item?.[langKey]) ||
      this.cleanValue(item?.[key]) ||
      this.cleanValue(item?.[fallbackKey]);
  }

  private pick(item: any, keys: string[]): string {
    for (const key of keys) {
      const value = this.cleanValue(item?.[key]);

      if (value) {
        return value;
      }
    }

    return '';
  }

  private cleanValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim();
  }

  private normalizeStars(value: any): number {
    const stars = Number(value || 5);

    if (Number.isNaN(stars)) {
      return 5;
    }

    return Math.min(Math.max(Math.round(stars), 1), 5);
  }

  private getAvatarLetter(name: string): string {
    const cleanName = (name || '').trim();

    if (!cleanName) {
      return '?';
    }

    return cleanName.charAt(0).toUpperCase();
  }

  private getCurrentLang(): 'ar' | 'en' {
    if (!this.isBrowser) {
      return 'en';
    }

    return localStorage.getItem('language') === 'ar' ? 'ar' : 'en';
  }
}